import { useQuery } from "@tanstack/react-query";
import { sessionKeys } from "../../shared/data/queryKeys";
import { ONE_MINUTE_MS } from "../../shared/constants";
import { normalizeKeys } from "../../shared/utils/normalize";
import { normalizeEntitlements } from "../../shared/utils/planEntitlements";
import { apiRpc } from "../../shared/data/supabaseApi";

function normalizeSessionContext(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      profile: null,
      team: null,
      organization: null,
      memberships: [],
      activeMembership: null,
      accessState: null,
      accessReason: null,
      pendingInviteEmail: null,
      pendingInviteId: null,
      pendingInviteTeamId: null,
      pendingInviteExpiresAt: null,
      pendingInviteStatus: null,
      allowedInternalAccess: false,
      orgRole: null,
      orgSeatCount: null,
      settings: {},
      counts: {},
      entitlements: normalizeEntitlements(null, null),
    };
  }
  const normalized = normalizeKeys(raw);
  let settings = normalized.settings || {};
  if (typeof settings === "string") {
    try {
      settings = JSON.parse(settings);
    } catch (_) {
      settings = {};
    }
  }
  const orgSeatCount =
    typeof normalized.orgSeatCount === "number" ? normalized.orgSeatCount : null;
  const organization = normalized.organization
    ? normalizeKeys(normalized.organization)
    : null;
  const entitlements = normalizeEntitlements(
    normalized.entitlements || organization?.entitlements || null,
    organization?.planTier || null,
    {
      seatsAllowed:
        typeof organization?.seatsAllowed === "number"
          ? organization.seatsAllowed
          : null,
    },
  );
  return {
    profile: normalized.profile ? normalizeKeys(normalized.profile) : null,
    team: normalized.team ? normalizeKeys(normalized.team) : null,
    organization: organization
      ? {
          ...organization,
          entitlements,
      }
      : null,
    activeMembership: normalized.activeMembership
      ? normalizeKeys(normalized.activeMembership)
      : null,
    accessState: normalized.accessState || null,
    accessReason: normalized.accessReason || null,
    pendingInviteEmail: normalized.pendingInviteEmail || null,
    pendingInviteId: normalized.pendingInviteId || null,
    pendingInviteTeamId: normalized.pendingInviteTeamId || null,
    pendingInviteExpiresAt: normalized.pendingInviteExpiresAt || null,
    pendingInviteStatus: normalized.pendingInviteStatus || null,
    allowedInternalAccess: Boolean(normalized.allowedInternalAccess),
    memberships: Array.isArray(normalized.memberships)
      ? normalized.memberships.map((entry) => normalizeKeys(entry))
      : [],
    orgRole: normalized.orgRole || null,
    orgSeatCount,
    settings: settings || {},
    counts:
      normalized.counts && typeof normalized.counts === "object"
        ? normalizeKeys(normalized.counts)
        : {},
    entitlements,
  };
}

export async function fetchSessionContext() {
  const { data, error } = await apiRpc("session_context");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return normalizeSessionContext(row);
}

export function useSessionContextQuery(options) {
  return useQuery({
    queryKey: sessionKeys.context(),
    queryFn: fetchSessionContext,
    staleTime: ONE_MINUTE_MS,
    ...options,
  });
}
